// Pure string constants — no Vue, Pinia, or other runtime side effects.
// Safe to import from Web Workers and other non-Vue contexts.

// CONTENT KEYS
export const articleKey: string = 'articles';
export const bonusReportKey: string = 'bonus-reports';
export const webinarKey: string = 'webinars';

// Alias keys for Articles
export const sectorSpotlightKey: string = 'sector-spotlight';
export const sectorFocusSpotlightKey: string = 'sector-focus-spotlight';
export const spotlightVideosKey: string = 'spotlight-videos';

export const courseKey: string = 'course';

export const contentWrapperKey: string = 'content-wrapper';
export const etfListingKey: string = 'etf-listing';
export const lessonsKey: string = 'lessons';
export const cptLessonsKey: string = 'cpt-lessons';
export const dmiLessonsKey: string = 'dmi-lessons';
export const dmiArticlesKey: string = 'dmi-articles';
export const dmtLessonsKey: string = 'dmt-lessons';
export const psLessonsKey: string = 'ps-lessons';
export const osLessonsKey: string = 'os-lessons';
export const sfLessonsKey: string = 'sf-lessons';
export const spLessonsKey: string = 'sp-lessons';
export const tamLessonsKey: string = 'tam-lessons';
export const tspLessonsKey: string = 'tsp-lessons';
export const majorIndexBreadthKey: string = 'major-index-breadth';
export const modelPortfolioKey: string = 'model-portfolio';
export const fiveStarModelPortfolioKey: string = '5-star-model-portfolio';
export const tspModelPortfolioKey: string = 'tsp-model-portfolio';
export const notFoundKey: string = '404';
export const nyseBpiKey: string = 'nyse-bpi';
export const portfolioBuilderKey: string = 'portfolio-builder';
export const positionSizeCalculatorKey: string = 'pos-size';
export const relativeStrengthMatrixKey: string = 'rs-matrix';
export const scannerKey: string = 'scanner';
export const sectorsKey: string = 'sectors';
export const sectorAlertsKey: string = 'sector-alerts';
export const sectorTreeKey: string = 'sector-tree';
export const shadowSpikeAlertKey: string = 'shadow-spike-alert';
export const spotlightVideoKey: string = 'spotlight-videos';
export const tradeAlertKey: string = 'trade-alerts';
export const fiveStarTradeAlertKey: string = '5-star-trade-alerts';
export const tradeAlertSearchKey: string = 'trade-alert-search';
export const tradeOfTheDayKey: string = 'trade-of-the-day';
export const tutorialsKey: string = 'tutorials';
export const osVimeoKey: string = 'os-vimeo-wrapper';
export const mttVimeoKey: string = 'mtt-vimeo-wrapper';
export const tamVimeoKey: string = 'tam-vimeo-wrapper';
export const vimeoKey: string = 'vimeo-wrapper';

// Discord keys
export const discordKey: string = 'discord';
export const discordRegistrationKey: string = 'discord-registration';

// Store refresh / sync keys
export const syncThrottlingKey: string = 'sync-throttle';
export const authorsSyncKey: string = 'authors-sync-meta';
export const categoriesSyncKey: string = 'categories-sync-meta';
export const productsSyncKey: string = 'products-sync-meta';
export const sectorsSyncKey: string = 'sectors-sync-meta';
// C4: rotated when the ticker catalog moved to the sentient REST endpoint, so a cache written before
// the cutover could never present a stale cooldown or ETag under the new key. The native cache layer
// (services/cache/cache-database.ts `applyUpgrade`) now drops every cooldown and ETag key inside the
// upgrade transaction, so a future rotation is not needed for that reason; the `-v2` name is kept
// because the ETag key is derived from it (`${ tickersSyncKey }:etag`, see dependency-sync.ts).
export const tickersSyncKey: string = 'tickers-sync-meta-v2';
// C5 (Option 1): the content-service ticker id -> symbol crosswalk sync (`ticker-crosswalk-sync-
// coordinator.ts`), kept solely to hydrate article/trade-alert/webinar ticker chips post-cutover —
// see docs/ticker-search-datasource-migration-plan.md v3 §C5.
export const simplePagesSyncKey: string = 'simple-pages-sync-meta';
export const sectorHistorySyncKey: string = 'sector-history-sync-meta';
// DP-10: the market date the sector-history full re-check was last scheduled for, and when it is due (0 = done).
export const sectorHistoryFullCheckDateKey: string = 'sector-history-full-check:dt';
export const sectorHistoryFullCheckDueKey: string = 'sector-history-full-check:due';
export const articlesSyncKey: string = 'articles-sync-meta';
export const bonusReportsSyncKey: string = 'bonus-reports-sync-meta';
export const tradeAlertsSyncKey: string = 'trade-alerts-sync-meta';
export const webinarsSyncKey: string = 'webinars-sync-meta';

// Context Key Constants
export const categoryContextKey: string = 'category';
export const contentSlugContextKey: string = 'content-slug';
export const courseSlugContextKey: string = 'course-slug';
export const placeholderContextKey: string = 'placeholder';
export const titleContextKey: string = 'title';
export const vimeoIdContextKey: string = 'vimeo-id';

// Portfolio Label Keys
export const openPositionsTitle: string = 'Open Positions';
export const closedPositionsTitle: string = 'Closed Positions';
export const openedPositionKey: string = 'opened';
export const closedPositionKey: string = 'closed';
export const initiatedKey: string = 'initiated';

// Portfolio Column Keys
export const actionsColumnKey: string = 'actions';
export const bpiStatusColumnKey: string = 'bpi-status';
export const closedColumnKey: string = closedPositionKey;
export const closedStockPriceColumnKey: string = 'closed_stock_price';
export const closedOptionsPriceColumnKey: string = 'closed_options_price';
export const initiatedColumnKey: string = 'initiated';
export const lastCheckPointColumnKey: string = 'last-check-point';
export const lastCloseColumnKey: string = 'last-close';
export const marketRelativeStrengthColumnKey: string = 'market-rs';
export const maxGainColumnKey: string = 'max_gain';
export const monthEndGainColumnKey: string = 'm_e_gain';
export const noteColumnKey: string = 'note';
export const openedStockPriceColumnKey: string = 'opened_stock_price';
export const openedOptionsPriceColumnKey: string = 'opened_options_price';
export const optionsGainsColumnKey: string = 'options_gains';
export const peerRelativeStrengthColumnKey: string = 'peer-rs';
export const priceTargetColumnKey: string = 'price_target';
export const stopLossColumnKey: string = 'stop_loss';
export const sectorColumnKey: string = 'sector';
export const sectorRelativeStrengthColumnKey: string = 'sector-rs';
export const stanceColumnKey: string = 'stance';
export const stockGainsColumnKey: string = 'stock_gains';
export const symbolColumnKey: string = 'symbol';

// Product Keys
export const cptProductKey: string = 'checkpoint-trader';
export const dmiProductKey: string = 'dark-money-indicator';
export const dmtProductKey: string = 'deep-market-trader';
export const mttProductKey: string = 'monthly-trend-trader';
export const osProductKey: string = 'options-soup';
export const psProductKey: string = 'profit-skimmer';
export const sfProductKey: string = 'sector-focus';
export const spProductKey: string = 'sector-prophets';
export const tamProductKey: string = 'technical-analysis-millionaire';
export const tspProductKey: string = 'true-sovereign-profits';

// Shadow Spike Scanner (code-defined product; NOT in the CMS catalog — see
// composables/products/shadow-spike-scanner.ts). `sssScannerKey`/`sssHistoryKey`
// are also global component names, so they must not collide with DMI's
// `scannerKey` ('scanner').
export const sssProductKey: string = 'shadow-spike-scanner';
export const sssScannerKey: string = 'sss-scanner';
export const sssHistoryKey: string = 'sss-history';

// URL tab tokens for the SSS workspace (`/shadow-spike?tab=…`). The actions-map
// key is what lands in the URL and it is per-product, so the short tokens don't
// collide with DMI's map (which also uses 'scanner'); the global component
// registry keeps using the prefixed component keys above.
export const sssScannerTabKey: string = 'scanner';
export const sssHistoryTabKey: string = 'history';

// Products whose composable declares `modelPortfolioKeys` (grep for it under
// composables/products/). The Scanner History "alerts from your services" filter
// lists exactly these; add here when a product gains a model portfolio.
export const SERVICE_POSITION_PRODUCT_SLUGS: readonly string[] = [
    cptProductKey, dmtProductKey, mttProductKey, psProductKey, sfProductKey
];

export function getContentTypeDisplayTextFromKey( key: string | undefined | null ): string {
    switch ( key ) {
        case articleKey:
            return "Article";
        case tradeAlertKey:
            return "Trade Alert";
        case bonusReportKey:
            return "Bonus Report";
        case webinarKey:
            return "Webinar";
        case null:
        case '':
        default:
            // Neutral fallback for null/empty/unrecognized keys. Previously
            // returned a debug placeholder that leaked to the UI on any
            // content with a missing type. This file is intentionally
            // side-effect-free (Web-Worker safe) — cannot pull in a
            // logger from here, so the mislabeled data isn't surfaced to
            // devs from this call site. If that becomes a real signal we
            // need, add logging at the CALLER instead.
            return "Content";
    }
}
